import * as React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CalendarCheck2,
  Clock,
  PhoneCall,
  TicketCheck,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../components/ui/chart';
import { Skeleton } from '../components/ui/skeleton';
import { useEffectiveness, type EffectivenessReport } from '../hooks/useEffectiveness';
import { HeaderActions } from '../components/HeaderActions';
import { cn } from '../lib/utils';

/**
 * Dashboard ist bewusst layout-stabil aufgebaut: alle Karten/Charts werden
 * sofort in finaler Höhe gerendert; während des Ladens fallen ihre Inhalte
 * auf Skeleton-Platzhalter zurück. Dadurch springt nichts, sobald die
 * Daten ankommen.
 */
export function HomePage() {
  const [days, setDays] = React.useState(30);
  const report = useEffectiveness(days);
  const data = report.data;

  return (
    <div className="flex flex-col gap-3 md:gap-4">
      <HeaderActions>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="h-8 w-[220px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Deine letzte Woche im Überblick</SelectItem>
            <SelectItem value="30">Dein letzter Monat im Überblick</SelectItem>
            <SelectItem value="90">Deine letzten drei Monate</SelectItem>
            <SelectItem value="365">Dein letztes Jahr</SelectItem>
          </SelectContent>
        </Select>
      </HeaderActions>

      <KpiRow report={data} />

      {/* Anrufe pro Tag — full-bleed seit der Termin-Typen-Karte (Top 5 nach
          Buchungen) auf Wunsch in die Termin-Typen-Detailseite verschoben
          wurde. Siehe Brief Section 2. */}
      <Card data-slot="card" className="overflow-hidden">
        <CardHeader className="space-y-0 pb-2">
          <CardTitle className="text-base">Deine Anrufe pro Tag</CardTitle>
          <CardDescription className="text-xs">
            So entwickeln sich deine Anrufe und Buchungen im Monat
          </CardDescription>
        </CardHeader>
        <CardContent className="px-2 pb-2 pt-0 sm:px-3 sm:pb-3">
          {data ? (
            <CallsAreaChart daily={data.daily} />
          ) : (
            <Skeleton className="h-[180px] w-full" />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiRow({ report }: { report: EffectivenessReport | undefined }) {
  const half = report ? Math.floor(report.daily.length / 2) || 1 : 1;
  const firstHalf = report?.daily.slice(0, half) ?? [];
  const secondHalf = report?.daily.slice(half) ?? [];
  const callsTrend = report
    ? trendPct(sum(firstHalf, (d) => d.calls), sum(secondHalf, (d) => d.calls))
    : null;
  const bookedTrend = report
    ? trendPct(sum(firstHalf, (d) => d.booked), sum(secondHalf, (d) => d.booked))
    : null;
  const minutesTrend = report
    ? trendPct(
        sum(firstHalf, (d) => d.calls), // proxy: minutes follow calls
        sum(secondHalf, (d) => d.calls),
      )
    : null;

  const s = report?.summary;

  return (
    <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card grid gap-3 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs sm:grid-cols-2 lg:grid-cols-4">
      <KpiCard
        icon={<PhoneCall className="size-4" />}
        title="Eingegangene Anrufe"
        value={s ? String(s.totalCalls) : null}
        trendPct={callsTrend}
        trendDescription={
          s
            ? `${s.bookedCalls} davon zu Terminen geworden`
            : 'Lade Anrufdaten …'
        }
      />
      <KpiCard
        icon={<Clock className="size-4" />}
        title="Gesprächszeit"
        value={s ? `${s.totalCallMinutes}` : null}
        unit={s ? 'min' : undefined}
        trendPct={minutesTrend}
        trendDescription={
          s && s.totalCalls > 0
            ? `Ø ${(s.totalCallMinutes / s.totalCalls).toFixed(1)} min pro Anruf`
            : 'Bald gehen die ersten Anrufe los'
        }
      />
      <KpiCard
        icon={<TicketCheck className="size-4" />}
        title="Offene Anfragen"
        value={s ? String(s.ticketsOpen) : null}
        trendDescription={
          s ? `${s.ticketsTotal} insgesamt im System` : 'Lade Anfragen …'
        }
        accent={s && s.ticketsOpen > 0 ? 'warning' : undefined}
      />
      <KpiCard
        icon={<CalendarCheck2 className="size-4" />}
        title="Gebuchte Termine"
        value={s ? String(s.bookedCalls) : null}
        trendPct={bookedTrend}
        trendDescription={
          s
            ? `${s.mirrorVerifiedCount} bestätigt in deinem Kalender`
            : 'Lade Buchungen …'
        }
      />
    </div>
  );
}

function KpiCard({
  icon,
  title,
  value,
  unit,
  trendPct,
  trendDescription,
  accent,
}: {
  icon?: React.ReactNode;
  title: string;
  value: string | null;
  unit?: string;
  trendPct?: number | null;
  trendDescription: string;
  accent?: 'success' | 'warning' | 'destructive';
}) {
  const accentClass: Record<NonNullable<typeof accent>, string> = {
    success: 'text-emerald-300',
    warning: 'text-amber-300',
    destructive: 'text-destructive',
  };
  const hasTrend = trendPct != null && Number.isFinite(trendPct);
  const isPositive = hasTrend && trendPct! >= 0;
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;
  const trendLabel = hasTrend
    ? `${isPositive ? '+' : ''}${trendPct!.toFixed(1)}%`
    : null;

  return (
    <Card className="@container/card" data-slot="card">
      <CardHeader className="px-4 pb-1 pt-4 sm:px-5 sm:pt-4">
        <CardDescription className="flex items-center gap-1.5 text-xs">
          {icon}
          {title}
        </CardDescription>
        <CardTitle
          className={cn(
            'text-2xl font-semibold tabular-nums @[250px]/card:text-3xl',
            accent && accentClass[accent],
          )}
        >
          {value == null ? (
            <Skeleton className="h-8 w-20" />
          ) : (
            <>
              {value}
              {unit && (
                <span className="ml-1 text-base font-normal text-muted-foreground">
                  {unit}
                </span>
              )}
            </>
          )}
        </CardTitle>
        {trendLabel && (
          <CardAction>
            <Badge variant="outline" className="text-[10px]">
              <TrendIcon className="size-3" />
              {trendLabel}
            </Badge>
          </CardAction>
        )}
      </CardHeader>
      <CardFooter className="flex-col items-start gap-0.5 px-4 pb-4 pt-0 text-xs sm:px-5 sm:pb-4">
        <div className="text-muted-foreground">{trendDescription}</div>
      </CardFooter>
    </Card>
  );
}

const callsChartConfig: ChartConfig = {
  calls: { label: 'Anrufe', color: 'hsl(172 66% 60%)' },
  booked: { label: 'Buchungen', color: 'hsl(38 92% 60%)' },
};

function CallsAreaChart({ daily }: { daily: EffectivenessReport['daily'] }) {
  if (daily.length === 0) {
    return (
      <div className="flex h-[180px] items-center justify-center text-sm text-muted-foreground">
        Sobald die ersten Anrufe da sind, siehst du sie hier.
      </div>
    );
  }
  return (
    <ChartContainer config={callsChartConfig} className="aspect-auto h-[180px] w-full">
      <AreaChart data={daily} margin={{ left: 4, right: 8, top: 8, bottom: 0 }}>
        <defs>
          <linearGradient id="grad-calls" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--color-calls)" stopOpacity={0.7} />
            <stop offset="95%" stopColor="var(--color-calls)" stopOpacity={0.05} />
          </linearGradient>
          <linearGradient id="grad-booked" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--color-booked)" stopOpacity={0.6} />
            <stop offset="95%" stopColor="var(--color-booked)" stopOpacity={0.04} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="day"
          tickLine={false}
          axisLine={false}
          tickMargin={6}
          tickFormatter={shortDate}
          minTickGap={32}
          fontSize={10}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={6}
          allowDecimals={false}
          width={24}
          fontSize={10}
        />
        <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dot" />} />
        <Area
          type="monotone"
          dataKey="calls"
          stroke="var(--color-calls)"
          fill="url(#grad-calls)"
          strokeWidth={2}
          name="calls"
          stackId="a"
        />
        <Area
          type="monotone"
          dataKey="booked"
          stroke="var(--color-booked)"
          fill="url(#grad-booked)"
          strokeWidth={2}
          name="booked"
          stackId="b"
        />
      </AreaChart>
    </ChartContainer>
  );
}

/**
 * TODO Brief Section 2: dieser Block (Termin-Typen Top 5 nach Buchungen)
 * gehört in die Termin-Typen-Detailansicht und wurde aus dem Dashboard
 * entfernt. Auskommentiert statt gelöscht, damit der Content dort
 * 1:1 wieder eingesetzt werden kann ohne Neuschreiben.
 *
 * function EventTypesCard({ report }: { report: EffectivenessReport | undefined }) {
 *   return (
 *     <Card data-slot="card">
 *       <CardHeader className="space-y-0 pb-2">
 *         <CardTitle className="text-base">Beliebteste Terminarten</CardTitle>
 *         <CardDescription className="text-xs">
 *           Deine Bestseller in der Buchung
 *         </CardDescription>
 *       </CardHeader>
 *       <CardContent className="space-y-2 pt-0">
 *         {report ? (
 *           report.perEventType.length === 0 ? (
 *             <div className="flex h-[160px] items-center justify-center text-sm text-muted-foreground">
 *               Hier erscheinen deine ersten gebuchten Termine.
 *             </div>
 *           ) : (
 *             <ul className="space-y-1.5">
 *               {report.perEventType.slice(0, 5).map((r) => (
 *                 <li
 *                   key={r.eventTypeId}
 *                   className="flex items-center justify-between rounded-md border border-border/60 px-2.5 py-1.5 text-sm"
 *                 >
 *                   <span className="truncate font-medium">{r.name}</span>
 *                   <span className="ml-2 shrink-0 tabular-nums text-muted-foreground">
 *                     {r.bookedCount}× · {r.plannedMinutes} min
 *                   </span>
 *                 </li>
 *               ))}
 *             </ul>
 *           )
 *         ) : (
 *           <div className="space-y-1.5">
 *             {[0, 1, 2, 3, 4].map((i) => (
 *               <Skeleton key={i} className="h-8 w-full" />
 *             ))}
 *           </div>
 *         )}
 */
// (closing tags of the commented-out card removed during clean-up)

function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function sum<T>(arr: T[], pick: (x: T) => number): number {
  let total = 0;
  for (const x of arr) total += pick(x) || 0;
  return total;
}

function trendPct(prev: number, curr: number): number | null {
  if (!Number.isFinite(prev) || !Number.isFinite(curr)) return null;
  if (prev === 0) {
    if (curr === 0) return 0;
    return null;
  }
  return ((curr - prev) / Math.abs(prev)) * 100;
}
